import type { PackLayoutNode } from '../../../lib/bubble/packNodeTypes';
import type {
  DrillLocation,
  ExplorationHighlight,
  ExplorationSecondary,
  ExplorationVolume,
  ViewMode,
} from '../../../types/exploration';

export interface BubbleZoomControls {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
}

export type BubbleZoomLevelInfo = {
  pct: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
};

export interface BubbleRenderOptions {
  view: ViewMode;
  /** Canonical current location — the only input click resolution needs. */
  location: DrillLocation;
  /** Positional projection of `location`, for display/tail-name comparisons only. */
  drillPath: string[];
  secondary: ExplorationSecondary;
  groupByAccount: boolean;
  tagKey?: string;
  /** Org/account volumes for validating by-account drill prefixes. */
  volumes?: ExplorationVolume[];
  canvasId: string;
  onDrill: (location: DrillLocation) => void;
  onDrillUp: () => void;
  /** Prefer `getCanDrillUp` so selection-only updates need not rebuild the canvas. */
  canDrillUp?: boolean;
  getCanDrillUp?: () => boolean;
  onExpandOverflow: (parentKey: string) => void;
  onSelectVolume: (volume: ExplorationVolume) => void;
  onSelectInstance: (instanceId: string, vols: ExplorationVolume[]) => void;
  onZoomLevel?: (info: BubbleZoomLevelInfo) => void;
  accountTotalSize: number;
  showTooltip: (
    html: string,
    x: number,
    y: number,
    variant?: 'group' | 'status' | 'volume'
  ) => void;
  hideTooltip: () => void;
  zoomControlsRef?: { current: BubbleZoomControls | null };
  /** Prefer getters so selection overlays can update without a full re-render.
   * `location` stays closed over at render time — selection that deepens location
   * must still change `drillPath` so the canvas rebuilds with a fresh location. */
  selectedVolumeId?: string | null;
  getSelectedVolumeId?: () => string | null | undefined;
  /** Re-applies canvas selection styling after hover on the selected volume. */
  reapplyVolumeSelection?: () => void;
  selectedInstanceId?: string | null;
  getSelectedInstanceId?: () => string | null | undefined;
  /** Re-applies canvas selection styling after hover on the selected instance. */
  reapplyInstanceSelection?: () => void;
  /** Active sidebar hover highlight (read on each pointer event). */
  getSidebarHighlight?: () => ExplorationHighlight;
  /** Re-applies sidebar emphasis after canvas pointer events. */
  reapplySidebarHighlight?: () => void;
  /** Called once the bubble DOM is ready (after drill transition when applicable). */
  onRenderComplete?: () => void;
  /** Toggle infrastructure group hover styling (bubble + chips). */
  applyInfraHover?: (node: PackLayoutNode | null, hovered: boolean) => void;
  /** Resolve a node's own bubble `<circle>` — lets chips (which paint over the
   * circle and own pointer events) forward hover into the circle's highlight
   * handlers instead of re-implementing them. */
  getBubbleCircleForNode?: (node: PackLayoutNode) => Element | null;
}

export const bubbleTransitionMs = 550;

export function resolveCanDrillUp(options: BubbleRenderOptions): boolean {
  return options.getCanDrillUp?.() ?? options.canDrillUp ?? options.drillPath.length > 0;
}

export function resolveSelectedVolumeId(options: BubbleRenderOptions): string | null | undefined {
  return options.getSelectedVolumeId?.() ?? options.selectedVolumeId;
}

export function resolveSelectedInstanceId(
  options: BubbleRenderOptions
): string | null | undefined {
  return options.getSelectedInstanceId?.() ?? options.selectedInstanceId;
}
